/**
 * Sorts the breakpoints values in ascending order.
 * @param {BreakpointValues} values The breakpoints values to sort
 * @returns {Partial<BreakpointValues>} The sorted breakpoints values
 * @example
 * import {sortBreakpointsValues} from "@civicplus/preamble-ui/utilities/createBreakpoints";
 *
 * const breakpoints = {
 *   xs: 0, // phone
 *   sm: 600, // tablet
 *   md: 900, // small laptop
 *   lg: 1200, // desktop
 *   xl: 1536, // large screen
 * };
 *
 * const sortedBreakpoints = sortBreakpointsValues(breakpoints);
 *
 */

export const sortBreakpointsValues = values => {
  const breakpointsAsArray = Object.keys(values).map(key => ({
    key,
    val: values[key]
  })) || [];
  // Sort in ascending order
  breakpointsAsArray.sort((breakpoint1, breakpoint2) => breakpoint1.val - breakpoint2.val);
  return breakpointsAsArray.reduce((acc, obj) => {
    return {
      ...acc,
      [obj.key]: obj.val
    };
  }, {});
};

// Overrides the default breakpoint type to include the query name, allowing us to modify the CSS query name

/**
 * Creates a new breakpoints object using the specified query name (default: "@media").
 * This allows us to use the same breakpoints object for custom queries, such as "@container".
 * @param {BreakpointsProps} breakpoints The breakpoints object to use
 * @returns {Breakpoints} The new breakpoints object
 * @example
 * import {createBreakpoints} from "@civicplus/preamble-ui/utilities/createBreakpoints";
 *
 * const breakpoints = {
 *  queryName: "@container"
 * };
 *
 * const containerBreakpoints = createBreakpoints(breakpoints);
 */
export default function createBreakpoints(breakpoints) {
  const {
    // The breakpoint **start** at this value.
    // For instance with the first breakpoint xs: [xs, sm).
    values = {
      xs: 0,
      // phone
      sm: 600,
      // tablet
      md: 900,
      // small laptop
      lg: 1200,
      // desktop
      xl: 1536 // large screen
    },
    unit = "px",
    step = 5,
    queryName = "@media"
  } = breakpoints;
  const sortedValues = sortBreakpointsValues(values);
  const keys = Object.keys(sortedValues);
  function up(key) {
    const value = typeof values[key] === "number" ? values[key] : key;
    return `${queryName} (min-width:${value}${unit})`;
  }
  function down(key) {
    const value = typeof values[key] === "number" ? values[key] : key;
    return `${queryName} (max-width:${value - step / 100}${unit})`;
  }
  function between(start, end) {
    const endIndex = typeof end === "number" ? -1 : keys.indexOf(end);
    return `${queryName} (min-width:${typeof values[start] === "number" ? values[start] : start}${unit}) and ` + `(max-width:${(endIndex !== -1 && typeof values[keys[endIndex]] === "number" ? values[keys[endIndex]] : end) - step / 100}${unit})`;
  }
  function only(key) {
    if (keys.indexOf(key) + 1 < keys.length) {
      return between(key, keys[keys.indexOf(key) + 1]);
    }
    return up(key);
  }
  return {
    keys,
    values: sortedValues,
    up,
    down,
    between,
    only,
    not: key => `${queryName} not ${up(key)}`
  };
}